import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import encountersService, {
  SOAPNote,
  DocumentTemplateOption,
  TemplateId,
} from "../../services/encountersService";
import AppLayout from "../../shared/components/AppLayout";
import TranscriptPlayer from "./components/TranscriptPlayer";
import SOAPNoteDisplay from "./components/SOAPNoteDisplay";
import DocumentDisplay from "./components/DocumentDisplay";
import SOAPEditModal from "./components/SOAPEditModal";

const EncounterDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [encounter, setEncounter] = useState<SOAPNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [copyingAll, setCopyingAll] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [templates, setTemplates] = useState<DocumentTemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateId>("soap");
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const isTemplateNote = !!encounter?.generated_document;


  const showToast = useCallback((message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  }, []);

  useEffect(() => {
    if (id) loadEncounter(id);
  }, [id]);

  // Load the doctor's templates once for the switcher
  useEffect(() => {
    encountersService
      .fetchTemplates()
      .then((opts) => {
        setTemplates(opts);
        // Preselect the doctor's default only if the note isn't already tied
        // to a specific template (that sync happens in loadEncounter).
        const def = opts.find((o) => o.is_default);
        setSelectedTemplate((prev) =>
          prev === "soap" && def ? def.id : prev,
        );
      })
      .catch(() => {
        /* templates are optional; ignore */
      });
  }, []);

  const loadEncounter = async (encounterId: string) => {
    try {
      setLoading(true);
      const data = await encountersService.fetchEncounterDetails(encounterId);
      setEncounter(data);
      // Sync the switcher to the note's current template
      setSelectedTemplate(data.template ?? "soap");
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load encounter");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = useCallback(async () => {
    if (!id || isRegenerating) return;
    setIsRegenerating(true);
    showToast("Regenerating note…");
    try {
      await encountersService.regenerateEncounter(id, selectedTemplate);
      const updated = await encountersService.pollUntilComplete(
        id,
        undefined,
        5000,
        600000,
      );
      setEncounter(updated);
      setSelectedTemplate(updated.template ?? "soap");
      showToast("Note regenerated successfully");
    } catch (err: any) {
      showToast(err.message || "Regeneration failed");
    } finally {
      setIsRegenerating(false);
    }
  }, [id, isRegenerating, selectedTemplate, showToast]);

  const handleCopyAll = useCallback(async () => {
    if (!encounter) return;

    // Helper to format individual fields (recurses into nested objects)
    const formatValue = (val: any): string => {
      if (Array.isArray(val)) {
        return val.length > 0
          ? val.map((item) => `• ${item}`).join("\n")
          : "Not documented";
      }
      if (typeof val === "object" && val !== null) {
        return Object.entries(val)
          .map(([k, v]) => {
            const label = k
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
            const inner = formatValue(v);
            return typeof v === "object" && v !== null
              ? `${label}:\n${inner
                  .split("\n")
                  .map((line) => `  ${line}`)
                  .join("\n")}`
              : `${label}: ${inner}`;
          })
          .join("\n");
      }
      return val || "Not documented";
    };

    // Helper to format a whole section
    const formatSection = (title: string, data: any): string => {
      let content = "";
      if (typeof data === "string") {
        content = data;
      } else if (typeof data === "object" && data !== null) {
        content = Object.entries(data)
          .map(([key, val]) => {
            const label = key
              .replace(/_/g, " ")
              .replace(/([A-Z])/g, " $1")
              .trim()
              .replace(/\b\w/g, (char) => char.toUpperCase());
            return `${label}:\n${formatValue(val)}`;
          })
          .join("\n\n");
      }
      return `${title.toUpperCase()}\n\n${content || "Not documented"}`;
    };

    const labelFromKey = (key: string): string =>
      key
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());

    const sections = encounter.generated_document
      ? Object.entries(encounter.generated_document).map(([key, val]) =>
          formatSection(labelFromKey(key), val),
        )
      : [
          formatSection("Subjective", encounter.subjective),
          formatSection("Objective", encounter.objective),
          formatSection("Assessment", encounter.assessment),
          formatSection("Plan", encounter.plan),
        ];

    const text = sections.join("\n\n");

    try {
      setCopyingAll(true);
      await navigator.clipboard.writeText(text);
      showToast("Full SOAP note copied to clipboard");
    } catch {
      showToast("Copy failed");
    } finally {
      setTimeout(() => setCopyingAll(false), 2000);
    }
  }, [encounter, showToast]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!encounter) return <p>Encounter not found</p>;

  return (
    <>

      {/* Toast */}
      {toast && (
        <div
          className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 bg-gray-900 text-white
          text-sm font-medium rounded-xl shadow-lg flex items-center gap-2 animate-fade-in"
        >
          <svg
            className="w-4 h-4 text-green-400 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          {toast}
        </div>
      )}

      <AppLayout active="encounters">
          <div className="h-full max-w-7xl mx-auto flex flex-col">
            <button
              onClick={() => navigate("/encounters")}
              className="mb-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-bold transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Encounters
            </button>

            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">
                Encounter Details
              </h1>
              <div className="flex gap-2">
                <span className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 text-sm font-medium text-gray-500">
                  Patient ID:{" "}
                  <span className="text-gray-900 font-bold">
                    {encounter.patient || "Unknown"}
                  </span>
                </span>
                <span className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 text-sm font-medium text-gray-500">
                  Date:{" "}
                  <span className="text-gray-900 font-bold">
                    {new Date(encounter.created_at).toLocaleDateString()}
                  </span>
                </span>
              </div>
            </div>

            {/* Audio Player */}
            {encounter.audio_archive && (
              <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-1">
                <TranscriptPlayer
                  audioUrl={encounter.audio_archive.download_url}
                  transcript={encounter.audio_archive.transcript}
                  filename={encounter.audio_archive.filename}
                  onAudioError={() => id && loadEncounter(id)}
                />
              </div>
            )}

            {/* Split Pane */}
            <div className="flex-1 flex flex-col lg:flex-row gap-6 overflow-hidden min-h-[500px]">
              {/* Left Pane - SOAP Note */}
              <div className="flex-1 flex flex-col overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="mb-6 border-b border-gray-100 pb-4 flex flex-col gap-4">
                  {/* Title row */}
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 shrink-0 bg-red-50 rounded-lg flex items-center justify-center text-red-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 truncate">
                      {isTemplateNote
                        ? encounter.template_name || "Generated Document"
                        : "Generated SOAP Note"}
                    </h2>

                    <div className="ml-auto flex items-center gap-2 shrink-0">
                      {/* Copy All */}
                      <button
                        onClick={handleCopyAll}
                        disabled={encounter.status !== "completed"}
                        title="Copy full note"
                        className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border
                          border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-400
                          transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {copyingAll ? (
                          <>
                            <svg
                              className="w-4 h-4 text-green-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-green-500 font-medium">
                              Copied
                            </span>
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                            <span className="hidden sm:inline">Copy All</span>
                          </>
                        )}
                      </button>

                      {/* Edit */}
                      <button
                        onClick={() => setShowEditModal(true)}
                        disabled={encounter.status !== "completed"}
                        title="Edit note"
                        className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border
                          border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-400
                          transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                    </div>
                  </div>

                  {/* Template toolbar row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <label
                      htmlFor="template-select"
                      className="text-xs font-semibold text-gray-400 uppercase tracking-wide shrink-0"
                    >
                      Template
                    </label>
                    <select
                      id="template-select"
                      value={String(selectedTemplate)}
                      onChange={(e) =>
                        setSelectedTemplate(
                          e.target.value === "soap"
                            ? "soap"
                            : Number(e.target.value),
                        )
                      }
                      disabled={isRegenerating}
                      title="Documentation template (applied on Regenerate)"
                      className="flex-1 min-w-[180px] text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700
                        hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {templates.map((t) => (
                        <option key={String(t.id)} value={String(t.id)}>
                          {t.name}
                        </option>
                      ))}
                    </select>

                    {/* Regenerate */}
                    <button
                      onClick={handleRegenerate}
                      disabled={isRegenerating}
                      title="Regenerate note from transcript"
                      className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border shrink-0
                        border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-400
                        transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRegenerating ? (
                        <>
                          <svg
                            className="w-4 h-4 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            />
                          </svg>
                          Regenerating…
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          Regenerate
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                  {encounter.status === "generating" ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
                      <svg
                        className="w-6 h-6 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        />
                      </svg>
                      <span className="text-sm">Generating note…</span>
                    </div>
                  ) : encounter.status === "failed" ? (
                    <div className="px-5 py-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                      <p className="font-semibold mb-1">Generation failed</p>
                      <p className="text-red-600">
                        {encounter.error_message ||
                          "An unknown error occurred while generating this note."}
                      </p>
                      <p className="text-red-500 text-xs mt-2">
                        Use Regenerate above to try again.
                      </p>
                    </div>
                  ) : isTemplateNote ? (
                    <DocumentDisplay
                      document={encounter.generated_document || {}}
                      onCopied={(label) =>
                        showToast(`${label} section copied to clipboard`)
                      }
                    />
                  ) : (
                    <SOAPNoteDisplay
                      soap={encounter}
                      copyMarkdown={encounter.copy_markdown || undefined}
                      onCopied={(label) =>
                        showToast(`${label} section copied to clipboard`)
                      }
                    />
                  )}
                </div>
              </div>

              {/* Right Pane - Conversation Transcript */}
              <div className="flex-1 flex flex-col overflow-hidden bg-gray-900 rounded-2xl shadow-xl p-6">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-800 pb-4 text-white">
                  <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center text-blue-400">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                      />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold">Conversation Transcript</h2>
                </div>
                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                  {encounter.structured_transcript?.turns?.length ? (
                    <div className="space-y-4 p-2">
                      {encounter.structured_transcript.turns.map((turn, i) => (
                        <div
                          key={i}
                          className={`flex gap-3 ${turn.speaker === "Patient" ? "flex-row-reverse" : "flex-row"}`}
                        >
                          <span
                            className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full h-fit mt-0.5
                              ${turn.speaker === "Doctor"
                                ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                                : turn.speaker === "Patient"
                                ? "bg-green-500/20 text-green-300 border border-green-500/30"
                                : "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                              }`}
                          >
                            {turn.speaker}
                          </span>
                          <p
                            className={`text-sm leading-relaxed px-3 py-2 rounded-xl max-w-[80%]
                              ${turn.speaker === "Doctor"
                                ? "text-blue-100 bg-blue-900/30"
                                : turn.speaker === "Patient"
                                ? "text-green-100 bg-green-900/30"
                                : "text-gray-300 bg-gray-800/50"
                              }`}
                          >
                            {turn.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm text-gray-300 font-mono leading-relaxed p-4 bg-gray-800/50 rounded-xl">
                      {encounter.transcript}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          </div>
      </AppLayout>

      {/* Edit Modal */}
      {showEditModal && (
        <SOAPEditModal
          encounter={encounter}
          onClose={() => setShowEditModal(false)}
          onSaved={(updated) => {
            setEncounter(updated);
            setShowEditModal(false);
            showToast("SOAP note updated successfully");
          }}
        />
      )}
    </>
  );
};

export default EncounterDetailPage;
